import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface EmailAccount {
  id: string;
  project_id: string;
  created_by: string;
  label: string;
  email_address: string;
  provider: string;
  imap_host: string | null;
  imap_port: number;
  imap_ssl: boolean;
  smtp_host: string | null;
  smtp_port: number;
  smtp_ssl: boolean;
  username: string | null;
  password_encrypted: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmailAccountInput {
  project_id: string;
  label: string;
  email_address: string;
  provider: string;
  imap_host?: string;
  imap_port?: number;
  imap_ssl?: boolean;
  smtp_host?: string;
  smtp_port?: number;
  smtp_ssl?: boolean;
  username?: string;
  password_encrypted?: string;
}

const PROVIDER_PRESETS: Record<string, { imap_host: string; imap_port: number; smtp_host: string; smtp_port: number }> = {
  gmail: { imap_host: 'imap.gmail.com', imap_port: 993, smtp_host: 'smtp.gmail.com', smtp_port: 465 },
  outlook: { imap_host: 'outlook.office365.com', imap_port: 993, smtp_host: 'smtp.office365.com', smtp_port: 587 },
  ionos: { imap_host: 'imap.ionos.de', imap_port: 993, smtp_host: 'smtp.ionos.de', smtp_port: 465 },
  strato: { imap_host: 'imap.strato.de', imap_port: 993, smtp_host: 'smtp.strato.de', smtp_port: 465 },
};

export { PROVIDER_PRESETS };

export const fetchEmailAccountsFromDb = async (): Promise<EmailAccount[]> => {
  const { data, error } = await supabase
    .from('email_accounts')
    .select('*')
    .order('created_at', { ascending: true });

  if (error) throw error;
  return ((data as unknown as EmailAccount[]) || []);
};

export const useEmailAccounts = () => {
  const queryClient = useQueryClient();
  const { user, initialized } = useAuthContext();

  const { data: accounts = [], isLoading: loading, refetch: fetchAccounts } = useQuery({
    queryKey: ['email-accounts'],
    queryFn: fetchEmailAccountsFromDb,
    enabled: !!user && initialized,
  });

  const addAccount = async (input: EmailAccountInput) => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('email_accounts')
      .insert({ ...input, created_by: user.id } as any)
      .select()
      .single();

    if (error) {
      toast.error('Fehler beim Speichern: ' + error.message);
      return null;
    }
    toast.success('E-Mail-Konto gespeichert');
    queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
    return data as unknown as EmailAccount;
  };

  const updateAccount = async (id: string, updates: Partial<EmailAccountInput> & { is_active?: boolean }) => {
    const { error } = await supabase
      .from('email_accounts')
      .update(updates as any)
      .eq('id', id);

    if (error) {
      toast.error('Fehler beim Aktualisieren: ' + error.message);
      return false;
    }
    queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
    return true;
  };

  const deleteAccount = async (id: string) => {
    const { error } = await supabase
      .from('email_accounts')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error('Fehler beim Löschen: ' + error.message);
      return false;
    }
    toast.success('E-Mail-Konto gelöscht');
    queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
    return true;
  };

  const getAccountsByProject = (projectId: string) => {
    return accounts.filter(a => a.project_id === projectId);
  };

  const getActiveAccounts = () => accounts.filter(a => a.is_active);

  return { accounts, loading, addAccount, updateAccount, deleteAccount, getAccountsByProject, getActiveAccounts, refetch: fetchAccounts };
};
